import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, updateDoc, doc, arrayUnion, arrayRemove, orderBy, limit } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useFamily } from '../contexts/FamilyContext';
import { getAllExperienceTypes } from '../data/experienceTypes';
import toast from 'react-hot-toast';
import Icon from '../components/shared/Icon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandHoldingHeart } from '@fortawesome/free-solid-svg-icons';
import { PageHeader } from '../design-system';

export default function Community() {
  const { user } = useAuth();
  const { family } = useFamily();
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    projectType: 'all', // Keep as projectType for Firestore field name
    familyStyle: 'all',
  });
  const [sortBy, setSortBy] = useState('upvotes'); // 'upvotes' or 'recent'

  const experienceTypes = getAllExperienceTypes();

  useEffect(() => {
    const constraints = [where('approved', '==', true)];
    
    if (filter.projectType !== 'all') {
      constraints.push(where('projectType', '==', filter.projectType));
    }

    let q = query(collection(db, 'communityTips'), ...constraints);
    
    // Note: Firestore doesn't support multiple where clauses on different fields easily
    // For MVP, we'll filter client-side for familyStyle
    const unsubscribe = onSnapshot(q, (snapshot) => {
      let tipsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Client-side filtering for familyStyle
      if (filter.familyStyle !== 'all') {
        tipsData = tipsData.filter(tip => tip.familyStyle === filter.familyStyle);
      }

      // Sort
      if (sortBy === 'upvotes') {
        tipsData.sort((a, b) => (b.upvotes || 0) - (a.upvotes || 0));
      } else {
        tipsData.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return bTime - aTime;
        });
      }

      setTips(tipsData);
      setLoading(false);
    });

    return unsubscribe;
  }, [filter.projectType, filter.familyStyle, sortBy]);

  const handleUpvote = async (tipId, isUpvoted) => {
    if (!user) {
      toast.error('Please sign in to upvote');
      return;
    }

    try {
      const tipRef = doc(db, 'communityTips', tipId);
      const tip = tips.find(t => t.id === tipId);
      
      if (isUpvoted) {
        // Remove upvote
        await updateDoc(tipRef, {
          upvotes: Math.max(0, (tip.upvotes || 0) - 1),
          upvotedBy: arrayRemove(user.uid),
        });
        toast.success('Upvote removed');
      } else {
        // Add upvote
        await updateDoc(tipRef, {
          upvotes: (tip.upvotes || 0) + 1,
          upvotedBy: arrayUnion(user.uid),
        });
        toast.success('Tip upvoted!');
      }
    } catch (error) {
      console.error('Error upvoting:', error);
      toast.error('Failed to upvote. Please try again.');
    }
  };

  const getFamilyStyleLabel = (style) => {
    const labels = {
      'just-beginning': 'Just Beginning',
      'holidays-only': 'Holidays Only',
      'dabbling': 'Dabbling',
      'pretty-active': 'Pretty Active',
    };
    return labels[style] || style;
  };

  return (
    <div className="min-h-screen bg-white pt-14 pb-20 md:pt-0 md:pb-0">
      <PageHeader
        title="Community Tips"
        subtitle="Real tips from families like yours. Find what works."
        maxWidth="1200px"
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 py-6 sm:py-8 md:py-10">

        {/* Filters */}
        <div className="bg-white rounded-card p-4 sm:p-6 mb-6 sm:mb-8 border border-border shadow-card">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-xs sm:text-sm font-semibold text-text-primary mb-2">
                Experience Type
              </label>
              <select
                value={filter.projectType}
                onChange={(e) => setFilter({ ...filter, projectType: e.target.value })}
                className="w-full px-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              >
                <option value="all">All Experiences</option>
                {experienceTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-xs sm:text-sm font-semibold text-text-primary mb-2">
                Family Style
              </label>
              <select
                value={filter.familyStyle}
                onChange={(e) => setFilter({ ...filter, familyStyle: e.target.value })}
                className="w-full px-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              >
                <option value="all">All Styles</option>
                <option value="just-beginning">Just Beginning</option>
                <option value="holidays-only">Holidays Only</option>
                <option value="dabbling">Dabbling</option>
                <option value="pretty-active">Pretty Active</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-xs sm:text-sm font-semibold text-text-primary mb-2">
                Sort By
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              >
                <option value="upvotes">Most Upvoted</option>
                <option value="recent">Most Recent</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tips List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="mb-4 animate-bounce"><Icon name="🏡" size="4x" className="text-primary" /></div>
            <div className="text-lg text-text-secondary">Loading tips...</div>
          </div>
        ) : tips.length === 0 ? (
          <div className="text-center py-12 bg-surface rounded-card border border-border">
            <div className="mb-4"><Icon name="💬" size="4x" className="text-text-secondary" /></div>
            <div className="text-lg font-semibold text-text-primary mb-2">No tips yet</div>
            <div className="text-sm text-text-secondary">
              Be the first to share a tip from your experience!
            </div>
          </div>
        ) : (
          <div className="space-y-4 sm:space-y-6">
            {tips.map((tip) => {
              const isUpvoted = user && tip.upvotedBy?.includes(user.uid);
              const experienceType = experienceTypes.find(t => t.id === tip.projectType);
              
              return (
                <div
                  key={tip.id}
                  className="bg-white rounded-card p-5 sm:p-6 border border-border shadow-card hover:shadow-card-hover transition-shadow"
                >
                  <div className="flex gap-4">
                    {/* Upvote Button */}
                    <div className="flex flex-col items-center">
                      <button
                        onClick={() => handleUpvote(tip.id, isUpvoted)}
                        className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg border-2 flex items-center justify-center transition-all ${
                          isUpvoted
                            ? 'bg-primary border-primary text-white'
                            : 'bg-white border-border text-text-secondary hover:border-primary hover:text-primary'
                        }`}
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                      <div className="text-xs sm:text-sm font-semibold text-text-primary mt-1">
                        {tip.upvotes || 0}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <p className="text-sm sm:text-base text-text-primary leading-relaxed mb-3 sm:mb-4">
                        {tip.content}
                      </p>
                      
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm text-text-secondary">
                        <span className="px-2 sm:px-3 py-1 bg-surface rounded-full">
                          {experienceType?.name || tip.projectType}
                        </span>
                        <span className="px-2 sm:px-3 py-1 bg-surface rounded-full">
                          {getFamilyStyleLabel(tip.familyStyle)}
                        </span>
                        {tip.kidsAges && tip.kidsAges.length > 0 && (
                          <span className="px-2 sm:px-3 py-1 bg-surface rounded-full">
                            Ages {tip.kidsAges.join(', ')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


